import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Base z-index values - maintaining hierarchy consistency with the app design
const BASE_Z_INDEX = {
  MASK: 1050,
  WRAP: 1060,
  CONTENT: 1070,
};

// Increment value for each new popup layer
const Z_INDEX_INCREMENT = 10;

// Interface for registered popup instances
interface PopupInstance {
  id: string;
  zIndex: number;
  isVisible: boolean;
}

// Context interface
interface PopupContextType {
  registerPopup: (id: string) => { zIndex: number; stackIndex: number };
  unregisterPopup: (id: string) => void;
  updatePopupVisibility: (id: string, isVisible: boolean) => void;
  getZIndex: (id: string) => number;
  getStackIndex: (id: string) => number;
  getActivePopups: () => PopupInstance[];
}

// Create context with default values
const PopupContext = createContext<PopupContextType>({
  registerPopup: () => ({ zIndex: BASE_Z_INDEX.CONTENT, stackIndex: 0 }),
  unregisterPopup: () => {},
  updatePopupVisibility: () => {},
  getZIndex: () => BASE_Z_INDEX.CONTENT,
  getStackIndex: () => 0,
  getActivePopups: () => [],
});

// Custom hook for components to use the popup context
export const usePopup = () => useContext(PopupContext);

export const PopupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State to track all registered popups
  const [popups, setPopups] = useState<PopupInstance[]>([]);

  // Register a new popup and assign it a z-index
  const registerPopup = useCallback((id: string) => {
    // Access state within the callback but return the expected values
    let resultZIndex = BASE_Z_INDEX.CONTENT;
    let resultStackIndex = 0;
    
    setPopups(prev => {
      const existingPopup = prev.find(popup => popup.id === id);
      
      if (existingPopup) {
        // Set our result values to existing popup values
        resultZIndex = existingPopup.zIndex;
        resultStackIndex = prev.indexOf(existingPopup);
        return prev; // Return unchanged state if popup already exists
      }

      // Calculate new z-index based on the number of already registered popups
      resultStackIndex = prev.length;
      resultZIndex = BASE_Z_INDEX.CONTENT + (resultStackIndex * Z_INDEX_INCREMENT);
      
      // Add the new popup
      return [...prev, { id, zIndex: resultZIndex, isVisible: false }];
    });
    
    // Return the expected object type
    return { zIndex: resultZIndex, stackIndex: resultStackIndex };
  }, []); // No dependencies to prevent render loops

  // Remove a popup when it's closed and no longer needed
  const unregisterPopup = useCallback((id: string) => {
    setPopups(prev => prev.filter(popup => popup.id !== id));
  }, []);

  // Update a popup's visibility state
  const updatePopupVisibility = useCallback((id: string, isVisible: boolean) => {
    setPopups(prev => 
      prev.map(popup => 
        popup.id === id ? { ...popup, isVisible } : popup
      )
    );
  }, []);

  // Get the z-index for a specific popup
  const getZIndex = useCallback((id: string) => {
    const popup = popups.find(p => p.id === id);
    return popup ? popup.zIndex : BASE_Z_INDEX.CONTENT;
  }, [popups]);

  // Get the stack index (position) for a specific popup
  const getStackIndex = useCallback((id: string) => {
    const index = popups.findIndex(p => p.id === id);
    return index >= 0 ? index : 0;
  }, [popups]);

  // Get all currently active popups
  const getActivePopups = useCallback(() => {
    return popups.filter(p => p.isVisible);
  }, [popups]);

  const contextValue = {
    registerPopup,
    unregisterPopup,
    updatePopupVisibility,
    getZIndex,
    getStackIndex,
    getActivePopups,
  };

  return (
    <PopupContext.Provider value={contextValue}>
      {children}
    </PopupContext.Provider>
  );
};
